import React, { useMemo, useState } from 'react'
import { ArrowLeft, ChevronRight, ChevronLeft, RotateCw, Check, Sparkles } from 'lucide-react'
import { CARDS } from './data.js'
import { tr, useLang } from '../lib/lang.js'
import { LangToggle } from '../lib/LangToggle.jsx'
import { rngFrom, shuffle } from '../lib/random.js'
import { awardBp } from '../lib/store.js'

const ROUND = 12
const CHECK = 3

export function RafagaGame({ onBack, onComplete }) {
  const [lang, setLang] = useLang()
  const [phase, setPhase] = useState('feed') // feed | check | done
  const [roundKey, setRoundKey] = useState(0)
  const [index, setIndex] = useState(0)
  const [answers, setAnswers] = useState([])
  const [qIndex, setQIndex] = useState(0)
  const [locked, setLocked] = useState(null)

  const deck = useMemo(() => {
    const rng = rngFrom((roundKey + 1) * 7919 + Date.now() % 100000)
    return shuffle(rng, CARDS).slice(0, ROUND)
  }, [roundKey])

  const quiz = useMemo(() => {
    const rng = rngFrom((roundKey + 1) * 104729)
    return shuffle(rng, deck).slice(0, CHECK).map((card) => {
      const others = shuffle(rng, CARDS.filter((c) => c.id !== card.id)).slice(0, 3)
      const options = shuffle(rng, [card, ...others])
      return { card, options, answerIndex: options.findIndex((o) => o.id === card.id) }
    })
  }, [deck, roundKey])

  const restart = () => {
    setPhase('feed')
    setIndex(0)
    setAnswers([])
    setQIndex(0)
    setLocked(null)
    setRoundKey((k) => k + 1)
  }

  const startCheck = () => {
    setPhase('check')
    setQIndex(0)
    setAnswers([])
    setLocked(null)
  }

  const answerCheck = (i) => {
    if (locked != null) return
    const correct = i === quiz[qIndex].answerIndex
    setLocked({ correct, i })
    setTimeout(() => {
      const nextAnswers = [...answers, correct]
      if (qIndex + 1 >= quiz.length) {
        const score = nextAnswers.filter(Boolean).length
        awardBp(score * 15 + ROUND * 2)
        onComplete?.(score * 15)
        setAnswers(nextAnswers)
        setPhase('done')
      } else {
        setAnswers(nextAnswers)
        setQIndex((q) => q + 1)
        setLocked(null)
      }
    }, 600)
  }

  // ── FEED ────────────────────────────────────────────────────────────────
  if (phase === 'feed') {
    const card = deck[index]
    const last = index === deck.length - 1
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={onBack}
            className="flex items-center gap-1.5 text-xs font-bold text-slate-400 hover:text-white cursor-pointer"
          >
            <ArrowLeft size={16} /> Volver
          </button>
          <LangToggle lang={lang} onChange={setLang} />
        </div>

        <div className="flex items-center justify-between text-[11px] text-slate-500">
          <span className="font-bold uppercase tracking-wider">Ráfaga</span>
          <span>
            {index + 1}/{deck.length}
          </span>
        </div>

        <button
          type="button"
          onClick={() => (last ? startCheck() : setIndex((i) => i + 1))}
          className="w-full text-left rounded-3xl bg-gradient-to-br from-[#121a2b] to-[#0b1220] border border-slate-800 p-6 min-h-[52vh] flex flex-col items-center justify-center gap-4 active:scale-[0.99] transition-transform cursor-pointer"
        >
          {card.img ? (
            <img
              src={card.img}
              alt={tr(lang, card.name.es, card.name.en)}
              className="w-40 h-40 sm:w-52 sm:h-52 rounded-2xl object-cover border border-slate-700"
            />
          ) : (
            <div className="w-40 h-40 sm:w-52 sm:h-52 rounded-3xl bg-slate-900/70 border border-slate-800 flex items-center justify-center text-[6rem] sm:text-[7rem] leading-none">
              {card.icon}
            </div>
          )}
          <h2 className="text-2xl sm:text-3xl font-black text-white text-center">
            {tr(lang, card.name.es, card.name.en)}
          </h2>
          <p className="text-sm sm:text-base text-slate-300 text-center max-w-md leading-relaxed">
            {tr(lang, card.fact.es, card.fact.en)}
          </p>
        </button>

        <div className="flex items-center justify-between">
          <button
            type="button"
            disabled={index === 0}
            onClick={() => setIndex((i) => Math.max(0, i - 1))}
            className="flex items-center gap-1 px-3 py-2 rounded-xl bg-slate-900 border border-slate-800 text-xs font-bold text-slate-300 disabled:opacity-40 cursor-pointer"
          >
            <ChevronLeft size={15} /> Atrás
          </button>
          {last ? (
            <button
              type="button"
              onClick={startCheck}
              className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-cyan-500 text-slate-950 text-sm font-black cursor-pointer shadow-lg shadow-cyan-500/25"
            >
              <Sparkles size={15} /> Pon a prueba
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setIndex((i) => i + 1)}
              className="flex items-center gap-1 px-4 py-2.5 rounded-xl bg-[#131b29] border border-slate-700 text-sm font-black text-white cursor-pointer"
            >
              Siguiente <ChevronRight size={15} />
            </button>
          )}
        </div>
        <p className="text-center text-[10px] text-slate-600">
          {tr(lang, 'Toca la ficha para avanzar', 'Tap the card to advance')}
        </p>
      </div>
    )
  }

  // ── CHECK ───────────────────────────────────────────────────────────────
  if (phase === 'check') {
    const item = quiz[qIndex]
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={onBack}
            className="flex items-center gap-1.5 text-xs font-bold text-slate-400 hover:text-white cursor-pointer"
          >
            <ArrowLeft size={16} /> Salir
          </button>
          <LangToggle lang={lang} onChange={setLang} />
        </div>
        <div className="text-[11px] text-slate-500 font-bold text-center">
          {tr(lang, '¿Quién es?', 'Who is it?')} {qIndex + 1}/{quiz.length}
        </div>

        <div className="p-6 rounded-2xl bg-[#0e131f] border border-slate-800 text-center">
          <div className="text-4xl mb-2">❓</div>
          <p className="text-base font-bold text-white">{tr(lang, item.card.fact.es, item.card.fact.en)}</p>
        </div>

        <div className="space-y-2.5">
          {item.options.map((o, i) => {
            const isAnswer = i === item.answerIndex
            const picked = locked?.i === i
            const cls =
              locked == null
                ? 'bg-[#131a29] border-slate-700 hover:border-cyan-500/60 text-white'
                : isAnswer
                  ? 'bg-emerald-950/60 border-emerald-500/50 text-emerald-200'
                  : picked
                    ? 'bg-rose-950/60 border-rose-500/50 text-rose-200'
                    : 'bg-[#0e131f] border-slate-800 text-slate-500'
            return (
              <button
                key={o.id}
                type="button"
                disabled={locked != null}
                onClick={() => answerCheck(i)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border text-sm font-bold text-left transition-all cursor-pointer disabled:cursor-default ${cls}`}
              >
                <span className="text-xl">{o.icon}</span>
                {tr(lang, o.name.es, o.name.en)}
              </button>
            )
          })}
        </div>
      </div>
    )
  }

  // ── DONE ────────────────────────────────────────────────────────────────
  const score = answers.filter(Boolean).length
  return (
    <div className="flex flex-col items-center justify-center min-h-[55vh] text-center space-y-5">
      <div className="w-16 h-16 rounded-full bg-cyan-500/20 border border-cyan-500/50 flex items-center justify-center">
        <Check size={30} className="text-cyan-300" />
      </div>
      <div>
        <h2 className="text-xl font-black text-white">
          {score}/{quiz.length} {tr(lang, 'aciertos', 'correct')}
        </h2>
        <p className="text-xs text-slate-400 mt-1">
          {tr(lang, `Repasaste ${deck.length} fichas`, `You reviewed ${deck.length} cards`)}
        </p>
      </div>
      <div className="flex gap-2 w-full max-w-xs">
        <button
          type="button"
          onClick={restart}
          className="flex-1 py-3 rounded-xl bg-cyan-500 text-slate-950 font-black text-sm cursor-pointer flex items-center justify-center gap-2"
        >
          <RotateCw size={16} /> Otra ráfaga
        </button>
        <button
          type="button"
          onClick={onBack}
          className="flex-1 py-3 rounded-xl bg-slate-900 border border-slate-800 text-white font-black text-sm cursor-pointer"
        >
          {tr(lang, 'Salir', 'Exit')}
        </button>
      </div>
    </div>
  )
}
